/** Живые события монитора: подписка на SSE, тосты, вибрация, подсветка шагов. */

import { useCallback, useEffect, useRef, useState } from 'react';

import type { MonitorEvent, Plan, StopAlert } from '@cdf/shared';

import { subscribeMonitor, type MonitorConnectionState } from '../api/events';
import { useToasts } from './useToasts';

export interface MonitorTick {
  atISO: string;
  carsSeen: number;
  eligible: number;
}

export interface MonitorApi {
  connection: MonitorConnectionState;
  lastTick: MonitorTick | null;
  /** Предупреждения по индексам шагов текущего плана. */
  alerts: ReadonlyMap<number, StopAlert>;
  /** Индекс шага, который надо подсветить прямо сейчас (свежее событие). */
  flashIndex: number | null;
  dismissAlert(index: number): void;
}

const ALERT_TITLES: Record<StopAlert['kind'], string> = {
  taken_by_other: 'Машину увели',
  refueled_by_other: 'Машину уже заправили',
  moved: 'Машина переехала',
  disappeared: 'Машина пропала из выдачи',
};

/** Тактильный сигнал: на улице тост можно и не заметить. */
function buzz(pattern: number | number[]): void {
  if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
    navigator.vibrate(pattern);
  }
}

export function useMonitor(options: {
  enabled: boolean;
  planId: string | null;
  onPlanUpdated(plan: Plan): void;
}): MonitorApi {
  const { enabled, planId, onPlanUpdated } = options;
  const toasts = useToasts();

  const [connection, setConnection] = useState<MonitorConnectionState>('connecting');
  const [lastTick, setLastTick] = useState<MonitorTick | null>(null);
  const [alerts, setAlerts] = useState<ReadonlyMap<number, StopAlert>>(new Map());
  const [flashIndex, setFlashIndex] = useState<number | null>(null);

  // Через ref, чтобы подписка не пересоздавалась на каждый ре-рендер экрана.
  const planIdRef = useRef(planId);
  planIdRef.current = planId;
  const onPlanUpdatedRef = useRef(onPlanUpdated);
  onPlanUpdatedRef.current = onPlanUpdated;
  const toastsRef = useRef(toasts);
  toastsRef.current = toasts;

  const flashTimer = useRef<number | undefined>(undefined);

  const handleEvent = useCallback((event: MonitorEvent) => {
    switch (event.type) {
      case 'tick':
        setLastTick({
          atISO: event.atISO,
          carsSeen: event.carsSeen,
          eligible: event.eligible,
        });
        break;

      case 'alert': {
        if (planIdRef.current && event.planId !== planIdRef.current) return;
        setAlerts((prev) => {
          const next = new Map(prev);
          next.set(event.stopIndex, event.alert);
          return next;
        });
        setFlashIndex(event.stopIndex);
        window.clearTimeout(flashTimer.current);
        flashTimer.current = window.setTimeout(() => setFlashIndex(null), 6_000);

        buzz(event.alert.kind === 'taken_by_other' ? [90, 60, 90, 60, 140] : 60);
        toastsRef.current.push({
          kind: event.alert.kind === 'moved' ? 'info' : 'danger',
          title: `Шаг ${event.stopIndex + 1}: ${ALERT_TITLES[event.alert.kind]}`,
          text: event.alert.message,
        });
        break;
      }

      case 'plan_updated':
        setAlerts(new Map());
        onPlanUpdatedRef.current(event.plan);
        toastsRef.current.push({
          kind: 'info',
          title: 'Маршрут пересчитан',
          text: event.reason,
        });
        break;

      case 'error':
        toastsRef.current.push({
          kind: 'danger',
          title: 'Монитор сообщает об ошибке',
          text: event.message,
        });
        break;

      default:
        break;
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const subscription = subscribeMonitor({
      onEvent: handleEvent,
      onStateChange: setConnection,
    });
    return () => {
      subscription.close();
      window.clearTimeout(flashTimer.current);
    };
  }, [enabled, handleEvent]);

  // Новый план — старые предупреждения не про него.
  useEffect(() => {
    setAlerts(new Map());
    setFlashIndex(null);
  }, [planId]);

  const dismissAlert = useCallback((index: number) => {
    setAlerts((prev) => {
      if (!prev.has(index)) return prev;
      const next = new Map(prev);
      next.delete(index);
      return next;
    });
  }, []);

  return { connection, lastTick, alerts, flashIndex, dismissAlert };
}
